import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Dismiss24Regular, Warning24Regular, Delete24Regular, Info24Regular } from "@fluentui/react-icons";
import "./ConfirmDialog.css";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: "danger" | "warning" | "info";
}

const variantIcon: Record<string, React.ReactElement> = {
  danger: <Delete24Regular />,
  warning: <Warning24Regular />,
  info: <Info24Regular />,
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  variant = "warning",
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="confirm-dialog-backdrop" onClick={onCancel} role="dialog" aria-modal="true">
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-dialog-content">
          <div className={`confirm-dialog-icon confirm-dialog-icon-${variant}`}>
            {variantIcon[variant]}
          </div>
          <h3 className="confirm-dialog-title">{title}</h3>
          <p className="confirm-dialog-message">{message}</p>
        </div>
        <div className="confirm-dialog-footer">
          <button className="confirm-dialog-btn-secondary" onClick={onCancel} autoFocus>
            {cancelLabel}
          </button>
          <button className={`confirm-dialog-btn-${variant}`} onClick={onConfirm}>
            {variantIcon[variant]}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
